import { useEffect, useState } from 'react';
import {
  CardContent,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useApplicationPdfStore } from '@lib';
import { ApplicationItem, Partner } from '@shared/api';
import { DateUtils, LocalStorageUtils, PermissionsUtils, RoutePaths, useRouteHook } from '@shared';
import { ApplicationType, PermissionLevel, PermissionType } from '@shared/types';
import { RouterNavLink, DelayedLinearProgress } from '@widgets';
import { PDFView } from '@widgets/PDFView/PDFView';

interface Props {
  loading: boolean;
  data: ApplicationItem[];
}

export const ApplicationsTable = ({ loading, data }: Props) => {
  const { navigate } = useRouteHook();

  const { loading: loadingPdf, file, getApplicationPdf, clear: clearApplicationPdf } = useApplicationPdfStore();

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [partners, setPartners] = useState<Partner[]>([]);
  // TODO: Card Anchors to sort menu item index issue
  const [cardAnchors, setCardAnchors] = useState<Array<Element | null>>(data.map(() => null));

  useEffect(() => {
    setPartners(LocalStorageUtils.getPartners() || []);
  }, []);

  // Open Card Menu Item
  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, idx: number) => {
    setCardAnchors((curAnchors) => {
      const anchors = curAnchors.slice();
      anchors.splice(idx, 1, e.currentTarget);
      return anchors;
    });
  };

  // Close Card Menu Item
  const handleClose = (idx: number) => {
    setCardAnchors((curAnchors) => {
      const anchors = curAnchors.slice();
      anchors.splice(idx, 1, null);
      return anchors;
    });
  };

  const openPdf = (applicationId: string, applicationType: string) => getApplicationPdf({ applicationId, applicationType });

  const columnsCount = partners && partners.length > 1 ? 6 : 5;

  const routeToDetails = (appId: string, applicationType: string, mainAppId: string | undefined) =>
    applicationType === ApplicationType.MAIN && !mainAppId
      ? RoutePaths.applicationsDetails.onNavigate(appId)
      : RoutePaths.applicationsSupplementaryDetails.onNavigate(mainAppId!, appId);

  const routeToEdit = (appId: number | string, applicationType: string, mainAppId: string | undefined) => {
    applicationType === ApplicationType.MAIN
      ? navigate(RoutePaths.applicationsEdit.onNavigate(appId))
      : navigate(RoutePaths.applicationsSupplementaryEdit.onNavigate(mainAppId!, appId), { state: { suppId: appId } });
  };

  if (loading) {
    return (
      <CardContent style={{ textAlign: 'center' }}>
        <Typography variant='body1'>Loading application details</Typography>
      </CardContent>
    );
  }
  if (!data.length) return <CardContent style={{ textAlign: 'center' }}>No data to display.</CardContent>;
  return (
    <>
      <DelayedLinearProgress loading={loadingPdf} />
      <Table aria-label='simple table' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '220px' }} align='left'>
              Application Ref №
            </TableCell>
            <TableCell style={{ width: '220px' }} align='left'>
              Full Name
            </TableCell>
            <TableCell style={{ width: '220px' }} align='left'>
              Passport/ID
            </TableCell>
            {partners && partners.length > 1 && (
              <TableCell style={{ width: '180px' }} align='center'>
                Partner
              </TableCell>
            )}
            <TableCell style={{ width: '150px' }} align='center'>
              Product
            </TableCell>
            <TableCell style={{ width: '180px' }} align='center'>
              Status
            </TableCell>
            <TableCell style={{ width: '160px' }} align='center'>
              Last Update
            </TableCell>
            <TableCell style={{ width: '170px' }} align='center'>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
            <TableRow key={idx} hover>
              <TableCell align='left' component='th' scope='row'>
                <Link component={RouterNavLink} to={routeToDetails(row.id, row.applicationType, row.mainAppId)} color='primary'>
                  {row.name}
                </Link>
              </TableCell>
              <TableCell>
                {row.firstName} {row.lastName}
              </TableCell>
              <TableCell>{row.passport_IDCardNo}</TableCell>
              {partners && partners.length > 1 && <TableCell align='center'>{row.partner}</TableCell>}
              <TableCell align='center'>
                {row.product || row.productName}
                {row.applicationType === ApplicationType.SUPPLEMENTARY ? ' Supplementary' : null}
              </TableCell>
              <TableCell align='center'>{row.status}</TableCell>
              <TableCell align='center'>{DateUtils.formatDate(row.processedDate)}</TableCell>
              <TableCell align='center'>
                <IconButton onClick={(e) => handleClick(e, idx)} size='large'>
                  <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={cardAnchors[idx]} open={Boolean(cardAnchors[idx])} onClick={() => handleClose(idx)}>
                  <MenuItem component={RouterNavLink} to={routeToDetails(row.id, row.applicationType, row.mainAppId)}>
                    View
                  </MenuItem>
                  {PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.EDIT) && row.status === 'Draft' && (
                    <MenuItem onClick={() => routeToEdit(row.appId || row.id, row.applicationType, row.mainAppId)}>Edit</MenuItem>
                  )}
                  {/* FILTER ENDPOINT AND GET ALL APPLICATIONS ENDPOINT SHOULD RETURN SAME DATA  */}
                  <MenuItem
                    onClick={() => (row.appId ? openPdf(row.appId.toString(), row.applicationType) : openPdf(row.id.toString(), row.applicationType))}
                  >
                    Print
                  </MenuItem>
                </Menu>
              </TableCell>
            </TableRow>
          ))}
          {!data.length ? (
            <TableRow>
              <TableCell colSpan={columnsCount} align='center'>
                No Records Found
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(+e.target.value);
        }}
      />
      {file?.data && <PDFView objectUrl={URL.createObjectURL(file.data)} onClose={clearApplicationPdf} />}
    </>
  );
};
